import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import Layout from '../components/Layout';
import PageHeader from '../components/layout/PageHeader';
import { Printer, Eye, Edit3, RotateCcw, CreditCard, Check } from 'lucide-react';

const TEMPLATES = [
  { id: 'classic', name: 'Classique', desc: 'Sobre et professionnel', bg: '#1a1a2e', text: '#fff', accent: '#e94560', sub: '#a0a0b8' },
  { id: 'modern', name: 'Moderne', desc: 'Epure avec accent cyan', bg: '#fff', text: '#1a1a2e', accent: '#0891b2', sub: '#64748b' },
  { id: 'gradient', name: 'Degrade', desc: 'Fond degrade vibrant', bg: 'linear-gradient(135deg, #0f172a, #1e3a5f, #0891b2)', text: '#fff', accent: '#38bdf8', sub: '#94a3b8' },
  { id: 'minimal', name: 'Minimaliste', desc: 'Blanc et net', bg: '#fafafa', text: '#111827', accent: '#dc2626', sub: '#6b7280' },
  { id: 'gold', name: 'Gold Premium', desc: 'Noir et or luxueux', bg: '#0a0a0a', text: '#fff', accent: '#d4a853', sub: '#8b8b8b' },
  { id: 'ocean', name: 'Ocean', desc: 'Bleu profond apaisant', bg: 'linear-gradient(135deg, #0c2d48, #145374, #2e8bc0)', text: '#fff', accent: '#00d4ff', sub: '#78b4c8' },
  { id: 'forest', name: 'Foret', desc: 'Vert nature elegant', bg: '#1a2e1a', text: '#fff', accent: '#4ade80', sub: '#7da87d' },
  { id: 'sunset', name: 'Coucher de soleil', desc: 'Orange chaleureux', bg: 'linear-gradient(135deg, #1a1a2e, #4a1942, #c84b31)', text: '#fff', accent: '#ff6b35', sub: '#c49898' },
  { id: 'corporate', name: 'Corporate', desc: 'Bleu marine pro', bg: '#0f1b2d', text: '#fff', accent: '#3b82f6', sub: '#94a3b8' },
  { id: 'pastel', name: 'Pastel Rose', desc: 'Doux et feminin', bg: '#fff5f5', text: '#4a2040', accent: '#e91e8c', sub: '#9b7a8f' },
];

const DEFAULT_INFO = {
  company_name: 'R.E.Mobiles',
  subtitle: 'Vente & Reparation de Telephones, Tablettes et Ordinateurs',
  owner_name: '',
  owner_title: 'Gerant',
  phone: '+594 694 27 40 51',
  email: 'contact@remobiles.com',
  address: '102 Avenue General de Gaulle, Porte 2',
  city: '97300 Cayenne, Guyane Francaise',
  website: 'www.remobiles.com',
  siret: '100 156 926 00014',
};

const BusinessCardPage = () => {
  const { api } = useAuth();
  const { t } = useLanguage();
  const [info, setInfo] = useState(DEFAULT_INFO);
  const [selectedTemplate, setSelectedTemplate] = useState(0);
  const [logoUrl, setLogoUrl] = useState('');
  const [editing, setEditing] = useState(false);
  const [showBack, setShowBack] = useState(false);
  const [logoTransparent, setLogoTransparent] = useState(false);

  // Noms/descriptions des modèles : traduits pour l'affichage via l'identifiant stable (id),
  // la logique (bg/accent/tmpl.id === ...) reste inchangée. Repli FR géré par t().
  const templateName = (tmpl) => t(`businessCard.tplName.${tmpl.id}`);
  const templateDesc = (tmpl) => t(`businessCard.tplDesc.${tmpl.id}`);

  useEffect(() => { fetchCompanySettings(); }, []);

  const fetchCompanySettings = async () => {
    try {
      const res = await api.get('/company-settings');
      const s = res.data;
      setInfo(prev => ({
        ...prev,
        company_name: s.company_name || prev.company_name,
        phone: s.phone || prev.phone,
        email: s.email || prev.email,
        address: s.address || prev.address,
        city: `${s.postal_code || '97300'} ${s.city || 'Cayenne'}, Guyane Francaise`,
        siret: s.siret || prev.siret,
      }));
      if (s.logo_url) {
        setLogoUrl(`${process.env.REACT_APP_BACKEND_URL}${s.logo_url}`);
      } else {
        setLogoUrl(`${process.env.REACT_APP_BACKEND_URL}/api/company-settings/logo/default`);
      }
    } catch (e) {
      console.error('Failed to load company settings', e);
    }
  };

  const handlePrintA4 = () => {
    const selectedTmpl = TEMPLATES[selectedTemplate];
    const printWindow = window.open('', '_blank');
    const cardsHtml = buildA4Html(selectedTmpl, info, logoUrl, logoTransparent, t);
    printWindow.document.write(cardsHtml);
    printWindow.document.close();
    setTimeout(() => { printWindow.print(); }, 800);
  };

  const updateField = (field, value) => setInfo(prev => ({ ...prev, [field]: value }));

  const selectedTmpl = TEMPLATES[selectedTemplate];

  const renderCardFront = (tmpl, w, h, fontSize = 1) => {
    const isGrad = tmpl.bg.startsWith('linear');
    return (
      <div style={{ width: w, height: h, padding: `${12*fontSize}px ${16*fontSize}px`, fontFamily: "'Segoe UI', Arial, sans-serif", display: 'flex', flexDirection: 'column', justifyContent: 'space-between', borderRadius: 6, overflow: 'hidden', position: 'relative', background: isGrad ? undefined : tmpl.bg, backgroundImage: isGrad ? tmpl.bg : undefined, color: tmpl.text }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8*fontSize }}>
          {logoUrl && (
            <img src={logoUrl} alt="Logo" style={{ width: 32*fontSize, height: 32*fontSize, borderRadius: 4, objectFit: 'contain', background: logoTransparent ? 'transparent' : (tmpl.id === 'modern' || tmpl.id === 'minimal' || tmpl.id === 'pastel' ? 'rgba(0,0,0,0.05)' : 'rgba(255,255,255,0.1)'), padding: 2 }} crossOrigin="anonymous" />
          )}
          <div>
            <div style={{ fontSize: 14*fontSize, fontWeight: 800, letterSpacing: 0.5, color: tmpl.accent }}>{info.company_name}</div>
            <div style={{ fontSize: 6*fontSize, color: tmpl.sub, maxWidth: 180*fontSize, lineHeight: 1.3 }}>{info.subtitle}</div>
          </div>
        </div>
        {info.owner_name && (
          <div style={{ marginTop: 6*fontSize }}>
            <div style={{ fontSize: 11*fontSize, fontWeight: 700 }}>{info.owner_name}</div>
            <div style={{ fontSize: 7*fontSize, color: tmpl.sub, textTransform: 'uppercase', letterSpacing: 1.2 }}>{info.owner_title}</div>
          </div>
        )}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginTop: 'auto' }}>
          <div style={{ fontSize: 7*fontSize, lineHeight: 1.5, color: tmpl.sub }}>
            <div>{info.phone}</div>
            <div>{info.email}</div>
            {info.website && <div style={{ color: tmpl.accent, fontWeight: 600 }}>{info.website}</div>}
          </div>
          <div style={{ fontSize: 6*fontSize, lineHeight: 1.5, textAlign: 'right', color: tmpl.sub }}>
            <div>{info.address}</div>
            <div>{info.city}</div>
          </div>
        </div>
        <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 3*fontSize, background: tmpl.accent }}></div>
      </div>
    );
  };

  const renderCardBack = (tmpl, w, h, fontSize = 1) => {
    const isGrad = tmpl.bg.startsWith('linear');
    const reverseBg = isGrad ? tmpl.bg.replace('135deg', '315deg') : tmpl.bg;
    return (
      <div style={{ width: w, height: h, fontFamily: "'Segoe UI', Arial, sans-serif", display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', borderRadius: 6, overflow: 'hidden', position: 'relative', background: isGrad ? undefined : reverseBg, backgroundImage: isGrad ? reverseBg : undefined, color: tmpl.text, textAlign: 'center', padding: 16*fontSize }}>
        {logoUrl && (
          <img src={logoUrl} alt="Logo" style={{ width: 48*fontSize, height: 48*fontSize, borderRadius: 8, objectFit: 'contain', marginBottom: 8*fontSize, background: logoTransparent ? 'transparent' : (tmpl.id === 'modern' || tmpl.id === 'minimal' || tmpl.id === 'pastel' ? 'rgba(0,0,0,0.05)' : 'rgba(255,255,255,0.1)'), padding: 4 }} crossOrigin="anonymous" />
        )}
        <div style={{ fontSize: 16*fontSize, fontWeight: 800, color: tmpl.accent, letterSpacing: 1 }}>{info.company_name}</div>
        <div style={{ fontSize: 7*fontSize, color: tmpl.sub, marginTop: 4*fontSize, maxWidth: 200*fontSize, lineHeight: 1.4 }}>{info.subtitle}</div>
        {info.siret && <div style={{ fontSize: 5.5*fontSize, color: tmpl.sub, marginTop: 8*fontSize, opacity: 0.7 }}>SIRET : {info.siret}</div>}
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3*fontSize, background: tmpl.accent }}></div>
      </div>
    );
  };

  return (
    <Layout>
      <div className="space-y-6 max-w-5xl mx-auto pb-8" data-testid="business-card-page">
        <PageHeader
          title={t('businessCard.title')}
          subtitle={t('businessCard.subtitle')}
          backTo="/dashboard"
          icon={CreditCard}
          testId="business-card-header"
          actions={
            <>
              <button onClick={() => setEditing(!editing)}
                className={`px-3 py-2 rounded-xl text-sm font-bold flex items-center gap-1.5 transition-colors ${editing ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30' : 'bg-white/10 text-gray-300 border border-white/10 hover:bg-white/20'}`}
                data-testid="toggle-edit-btn">
                <Edit3 className="w-4 h-4" /> {editing ? t('businessCard.previewBtn') : t('businessCard.edit')}
              </button>
              <button onClick={handlePrintA4} className="px-3 py-2 rounded-xl bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 text-sm font-bold flex items-center gap-1.5 hover:bg-emerald-500/30" data-testid="print-cards-btn">
                <Printer className="w-4 h-4" /> {t('businessCard.printA4')}
              </button>
            </>
          }
        />

      {/* Template Selector - 10 templates */}
      <div className="glass p-4" data-testid="template-selector">
        <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-3">{t('businessCard.chooseTemplate', { count: TEMPLATES.length })}</h3>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
          {TEMPLATES.map((tmpl, i) => (
            <button key={tmpl.id} onClick={() => setSelectedTemplate(i)}
              className={`p-2.5 rounded-xl border text-left transition-all ${i === selectedTemplate ? 'border-cyan-500 bg-cyan-500/10 ring-1 ring-cyan-500/30' : 'border-white/10 bg-white/5 hover:bg-white/10'}`}
              data-testid={`template-${tmpl.id}`}>
              <div className="flex items-center gap-2 mb-1">
                <div className="w-4 h-4 rounded-full border border-white/20" style={{ background: tmpl.accent }}></div>
                <div className="text-xs font-bold text-white truncate">{templateName(tmpl)}</div>
                {i === selectedTemplate && <Check className="w-3 h-3 text-cyan-400 ml-auto shrink-0" />}
              </div>
              <div className="text-[9px] text-gray-500">{templateDesc(tmpl)}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Logo transparency toggle */}
      <div className="glass p-4 flex items-center gap-3">
        <label className="text-sm text-gray-300 flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={logoTransparent} onChange={e => setLogoTransparent(e.target.checked)}
            className="w-4 h-4 rounded bg-white/10 border-white/20 accent-cyan-500" />
          {t('businessCard.logoTransparent')}
        </label>
      </div>

      {/* Editor */}
      {editing && (
        <div className="glass p-5" data-testid="card-editor">
          <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-4">{t('businessCard.cardInfo')}</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[
              { key: 'company_name', label: t('businessCard.field.companyName') },
              { key: 'subtitle', label: t('businessCard.field.subtitle') },
              { key: 'owner_name', label: t('businessCard.field.ownerName') },
              { key: 'owner_title', label: t('businessCard.field.ownerTitle') },
              { key: 'phone', label: t('businessCard.field.phone') },
              { key: 'email', label: t('businessCard.field.email') },
              { key: 'address', label: t('businessCard.field.address') },
              { key: 'city', label: t('businessCard.field.city') },
              { key: 'website', label: t('businessCard.field.website') },
              { key: 'siret', label: t('businessCard.field.siret') },
            ].map(f => (
              <div key={f.key}>
                <label className="text-xs text-gray-500 mb-1 block">{f.label}</label>
                <input type="text" value={info[f.key] || ''} onChange={e => updateField(f.key, e.target.value)}
                  className="input-dark w-full py-2 px-3 text-sm" data-testid={`input-${f.key}`} />
              </div>
            ))}
          </div>
          <button onClick={() => setInfo(DEFAULT_INFO)} className="mt-3 text-xs text-gray-500 hover:text-gray-300 flex items-center gap-1">
            <RotateCcw className="w-3 h-3" /> {t('businessCard.reset')}
          </button>
        </div>
      )}

      {/* Preview */}
      <div className="glass p-6" data-testid="card-preview">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider flex items-center gap-2">
            <Eye className="w-4 h-4" /> {t('businessCard.previewTitle', { name: templateName(selectedTmpl) })}
          </h3>
          <button onClick={() => setShowBack(!showBack)}
            className="text-xs text-cyan-400 hover:text-cyan-300 flex items-center gap-1" data-testid="toggle-face-btn">
            <RotateCcw className="w-3.5 h-3.5" />
            {showBack ? t('businessCard.viewFront') : t('businessCard.viewBack')}
          </button>
        </div>
        <div className="flex justify-center">
          <div className="rounded-xl shadow-2xl overflow-hidden border border-white/10" style={{ boxShadow: '0 20px 60px rgba(0,0,0,0.5)' }}>
            {showBack ? renderCardBack(selectedTmpl, 340, 200) : renderCardFront(selectedTmpl, 340, 200)}
          </div>
        </div>
        <p className="text-center text-[10px] text-gray-600 mt-3">{t('businessCard.standardFormat')}</p>
      </div>

      {/* All Templates Mini Preview */}
      <div className="glass p-5" data-testid="all-templates-preview">
        <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-4">{t('businessCard.allTemplates')}</h3>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          {TEMPLATES.map((tmpl, i) => (
            <div key={tmpl.id} className="cursor-pointer" onClick={() => setSelectedTemplate(i)}>
              <div className={`rounded-lg overflow-hidden border transition-all ${i === selectedTemplate ? 'border-cyan-500 ring-1 ring-cyan-500/30' : 'border-white/10 hover:border-white/30'}`}>
                {renderCardFront(tmpl, 160, 95, 0.55)}
              </div>
              <div className="text-center mt-1 text-[9px] text-gray-500">{templateName(tmpl)}</div>
            </div>
          ))}
        </div>
      </div>
      </div>
    </Layout>
  );
};

function buildA4Html(tmpl, info, logoUrl, logoTransparent, t) {
  const isGrad = tmpl.bg.startsWith('linear');
  const bgCss = isGrad ? `background-image: ${tmpl.bg};` : `background: ${tmpl.bg};`;
  const reverseBg = isGrad ? tmpl.bg.replace('135deg', '315deg') : tmpl.bg;
  const reverseBgCss = isGrad ? `background-image: ${reverseBg};` : `background: ${reverseBg};`;
  const logoBg = logoTransparent ? 'transparent' : (tmpl.id === 'modern' || tmpl.id === 'minimal' || tmpl.id === 'pastel' ? 'rgba(0,0,0,0.05)' : 'rgba(255,255,255,0.1)');
  const logoHtml = logoUrl ? `<img src="${logoUrl}" crossorigin="anonymous" style="width:11mm;height:11mm;border-radius:2mm;object-fit:contain;background:${logoBg};padding:1mm;" />` : '';
  const logoBackHtml = logoUrl ? `<img src="${logoUrl}" crossorigin="anonymous" style="width:15mm;height:15mm;border-radius:2.5mm;object-fit:contain;background:${logoBg};padding:1.5mm;margin-bottom:2mm;" />` : '';

  const frontCard = `
    <div class="card" style="${bgCss} color:${tmpl.text};">
      <div style="display:flex;align-items:center;gap:3mm;">
        ${logoHtml}
        <div>
          <div style="font-size:4.5mm;font-weight:800;color:${tmpl.accent};letter-spacing:0.2mm;">${info.company_name}</div>
          <div style="font-size:2mm;color:${tmpl.sub};max-width:50mm;line-height:1.3;">${info.subtitle}</div>
        </div>
      </div>
      ${info.owner_name ? `<div style="margin-top:2mm;"><div style="font-size:3.5mm;font-weight:700;">${info.owner_name}</div><div style="font-size:2mm;color:${tmpl.sub};text-transform:uppercase;letter-spacing:0.5mm;">${info.owner_title}</div></div>` : ''}
      <div style="display:flex;justify-content:space-between;align-items:flex-end;margin-top:auto;">
        <div style="font-size:2mm;line-height:1.5;color:${tmpl.sub};">
          <div>${info.phone}</div>
          <div>${info.email}</div>
          ${info.website ? `<div style="color:${tmpl.accent};font-weight:600;">${info.website}</div>` : ''}
        </div>
        <div style="font-size:1.8mm;line-height:1.5;text-align:right;color:${tmpl.sub};">
          <div>${info.address}</div>
          <div>${info.city}</div>
        </div>
      </div>
      <div style="position:absolute;bottom:0;left:0;right:0;height:1mm;background:${tmpl.accent};"></div>
    </div>`;

  const backCard = `
    <div class="card" style="${reverseBgCss} color:${tmpl.text}; display:flex;flex-direction:column;justify-content:center;align-items:center;text-align:center;">
      ${logoBackHtml}
      <div style="font-size:5mm;font-weight:800;color:${tmpl.accent};letter-spacing:0.3mm;">${info.company_name}</div>
      <div style="font-size:2mm;color:${tmpl.sub};margin-top:1.5mm;max-width:55mm;line-height:1.4;">${info.subtitle}</div>
      ${info.siret ? `<div style="font-size:1.8mm;color:${tmpl.sub};margin-top:3mm;opacity:0.7;">SIRET : ${info.siret}</div>` : ''}
      <div style="position:absolute;top:0;left:0;right:0;height:1mm;background:${tmpl.accent};"></div>
    </div>`;

  // Page 1: 10 recto cards (2 columns x 5 rows)
  // Page 2: 10 verso cards (2 columns x 5 rows) - aligned to match recto
  let rectoCards = '';
  let versoCards = '';
  for (let i = 0; i < 10; i++) {
    rectoCards += frontCard;
    versoCards += backCard;
  }

  return `<!DOCTYPE html><html><head><title>${t('businessCard.printTitle')} - ${info.company_name}</title>
<style>
  @page { size: A4; margin: 5mm 10mm; }
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: 'Segoe UI', Arial, sans-serif; }
  .page { width: 190mm; height: 275mm; display: grid; grid-template-columns: 85mm 85mm; grid-template-rows: repeat(5, 55mm); gap: 0mm 20mm; page-break-after: always; justify-content: center; }
  .page:last-child { page-break-after: auto; }
  .card { width: 85mm; height: 55mm; overflow: hidden; position: relative; padding: 4mm 5mm; font-family: 'Segoe UI', Arial, sans-serif; display: flex; flex-direction: column; justify-content: space-between; border: 0.2mm solid rgba(0,0,0,0.08); border-radius: 1mm; }
  .page-label { text-align: center; font-size: 2.5mm; color: #bbb; margin-bottom: 0mm; font-weight: bold; text-transform: uppercase; letter-spacing: 1mm; line-height: 4mm; }
</style></head><body>
<div class="page-label">${t('businessCard.recto')}</div>
<div class="page">${rectoCards}</div>
<div class="page-label">${t('businessCard.verso')}</div>
<div class="page">${versoCards}</div>
</body></html>`;
}

export default BusinessCardPage;
